import { Fragment } from "react"
import { Link } from "react-router-dom"

/**
 * A titled group of settings rows, rendered as one card.
 */
export function SettingsSection({ title, children, id }) {
    return (
        <section className="settings-section" aria-labelledby={`${id}-title`}>
            <h2 className="settings-section-title" id={`${id}-title`}>
                {title}
            </h2>
            <div className="settings-card">{children}</div>
        </section>
    )
}

/**
 * One row inside a SettingsSection: icon tile, label, optional sub-text and
 * an optional right-hand control.
 *
 * With `to` it renders a router link, with `onClick` a button, otherwise a
 * static row. A row with no `right` control and an action shows a chevron.
 * `subTone` colours the sub-text ("danger" for a problem the user can fix).
 */
export default function SettingRow({
    icon,
    label,
    sub,
    subTone,
    right,
    to,
    onClick,
    danger = false,
    disabled = false,
}) {
    const body = (
        <>
            <span className="setting-icon" aria-hidden="true">
                <i className={`bi ${icon}`}></i>
            </span>
            <span className="setting-text">
                <span className="setting-label">{label}</span>
                {sub && (
                    <span
                        className={`setting-sub${
                            subTone ? ` setting-sub-${subTone}` : ""
                        }`}
                    >
                        {sub}
                    </span>
                )}
            </span>
            {right ? (
                <span className="setting-right">{right}</span>
            ) : (
                (to || onClick) && (
                    <i
                        className="bi bi-chevron-right setting-chevron"
                        aria-hidden="true"
                    ></i>
                )
            )}
        </>
    )

    const className = `setting-row${danger ? " setting-row-danger" : ""}${
        to || onClick ? " setting-row-action" : ""
    }`

    if (to) {
        return (
            <Link className={className} to={to}>
                {body}
            </Link>
        )
    }

    if (onClick) {
        return (
            <button
                type="button"
                className={className}
                onClick={onClick}
                disabled={disabled}
            >
                {body}
            </button>
        )
    }

    return <div className={className}>{body}</div>
}

/**
 * Single-choice control shown as a joined button group.
 * `name` must be unique on the page (it ties the radio inputs together).
 */
export function SegmentedControl({ name, label, options, value, onChange, disabled }) {
    return (
        <div className="btn-group" role="radiogroup" aria-label={label}>
            {options.map((option) => (
                <Fragment key={option.value}>
                    <input
                        type="radio"
                        className="btn-check"
                        name={name}
                        id={`${name}-${option.value}`}
                        checked={value === option.value}
                        onChange={() => onChange(option.value)}
                        disabled={disabled}
                    />
                    <label
                        className="btn btn-sm btn-outline-primary"
                        htmlFor={`${name}-${option.value}`}
                    >
                        {option.label}
                    </label>
                </Fragment>
            ))}
        </div>
    )
}
